/**
 * Background reconcile degraded state (#445).
 * When local transport tiers exhaust, background polling stops and probes
 * with exponential backoff until a local tier answers again.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';

export const RECONCILE_BACKOFF_MS = [30_000, 60_000, 300_000] as const;

let _degraded = false;
let _backoffAttempt = 0;
let _probeTimer: ReturnType<typeof setTimeout> | null = null;

export function isReconcileDegraded(): boolean {
  return _degraded;
}

export function getReconcileBackoffAttemptForTests(): number {
  return _backoffAttempt;
}

export function markReconcileDegraded(source: string): void {
  const wasDegraded = _degraded;
  _degraded = true;
  if (!wasDegraded) {
    _backoffAttempt = 0;
    diagnosticLog('warn', 'playback', '[ReconcileDegraded] background reconcile halted — local tiers exhausted', {
      source,
    });
  }
}

export function clearReconcileDegraded(source: string): void {
  if (!_degraded) return;
  _degraded = false;
  _backoffAttempt = 0;
  if (_probeTimer) {
    clearTimeout(_probeTimer);
    _probeTimer = null;
  }
  diagnosticLog('info', 'playback', '[ReconcileDegraded] local tier recovered — background reconcile re-enabled', {
    source,
  });
}

export function scheduleReconcileRecoveryProbe(onProbe: () => void): void {
  if (_probeTimer) {
    clearTimeout(_probeTimer);
    _probeTimer = null;
  }
  const delay = RECONCILE_BACKOFF_MS[
    Math.min(_backoffAttempt, RECONCILE_BACKOFF_MS.length - 1)
  ];
  _backoffAttempt += 1;
  _probeTimer = setTimeout(() => {
    _probeTimer = null;
    onProbe();
  }, delay);
}

export function resetReconcileDegradedForTests(): void {
  _degraded = false;
  _backoffAttempt = 0;
  if (_probeTimer) {
    clearTimeout(_probeTimer);
    _probeTimer = null;
  }
}
